import type { BacktestExitReason, BacktestStats } from '@fleece/models';
import { max } from 'd3-array';
import { scaleBand, scaleLinear } from 'd3-scale';
import { arc, pie } from 'd3-shape';
import { useMemo, useState } from 'react';
import { OutcomeMarker } from '@/components/ui';
import { useElementWidth } from '@/hooks/use-element-width';
import type { StackedBin } from '@/lib/histogram';
import { OUTCOMES, type OutcomeStyle } from '@/lib/outcomes';

const STACK_ORDER: ReadonlyArray<BacktestExitReason> = ['take_profit', 'expired', 'stop_loss'];

/**
 * How the positions ended, as a share of all of them. Four segments at most, each
 * labelled with its count in the legend beside it, so the donut is a glance and the
 * numbers are the reading.
 */
export function OutcomeDonut({ stats }: { stats: BacktestStats }) {
  const counts: Record<BacktestExitReason, number> = { take_profit: stats.takeProfits, stop_loss: stats.stopLosses, expired: stats.expired, open: stats.open };
  const slices = OUTCOMES.filter((outcome) => counts[outcome.reason] > 0);
  const size = 104;
  const arcs = pie<OutcomeStyle>()
    .sort(null)
    .value((outcome) => counts[outcome.reason])
    .padAngle(slices.length > 1 ? 0.02 : 0)(slices);
  const path = arc<(typeof arcs)[number]>()
    .innerRadius(size / 2 - 16)
    .outerRadius(size / 2)
    .cornerRadius(2);

  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`} role="img" aria-label="Positions by outcome" className="shrink-0">
        {arcs.map((a) => (
          <path key={a.data.reason} d={path(a) ?? ''} fill={a.data.color} stroke="var(--surface)" strokeWidth={1} />
        ))}
        <text textAnchor="middle" dy="-0.1em" fontSize={16} fontWeight={600} fill="var(--ink)">
          {stats.positions}
        </text>
        <text textAnchor="middle" dy="1.3em" fontSize={10} fill="var(--muted)">
          positions
        </text>
      </svg>
      <ul className="flex flex-col gap-1 text-xs tabular">
        {OUTCOMES.map((outcome) => (
          <li key={outcome.reason} className="flex items-center gap-1.5">
            <OutcomeMarker color={outcome.color} shape={outcome.shape} />
            <span className="w-20 text-ink-2">{outcome.label}</span>
            <span className="w-8 text-right font-semibold">{counts[outcome.reason]}</span>
            <span className="w-10 text-right text-muted">{stats.positions === 0 ? '' : `${Math.round((counts[outcome.reason] / stats.positions) * 100)}%`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface StackedHistogramProps {
  readonly bins: ReadonlyArray<StackedBin>;
  readonly label: string;
  readonly formatBin: (bin: StackedBin) => string;
  readonly height?: number;
}

/**
 * Closed positions in bins, each bar stacked by how they ended. One y axis, counts; the
 * reading for a bar is in the line above the plot on hover or tap, which works the same
 * with a pointer and a thumb.
 */
export function StackedHistogram({ bins, label, formatBin, height = 140 }: StackedHistogramProps) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | undefined>(undefined);
  const margin = { top: 4, right: 4, bottom: 18, left: 26 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = height - margin.top - margin.bottom;

  const { x, y, ticks } = useMemo(() => {
    const xScale = scaleBand<number>()
      .domain(bins.map((_, i) => i))
      .range([0, innerWidth])
      .paddingInner(0.12);
    const yScale = scaleLinear()
      .domain([0, max(bins, (bin) => bin.total) ?? 1])
      .range([innerHeight, 0])
      .nice();
    return { x: xScale, y: yScale, ticks: yScale.ticks(3).filter(Number.isInteger) };
  }, [bins, innerWidth, innerHeight]);

  const labelEvery = Math.max(1, Math.ceil(bins.length / Math.max(1, Math.floor(innerWidth / 44))));
  const shown = active === undefined ? undefined : bins[active];

  return (
    <div>
      <div className="flex min-h-4 flex-wrap gap-x-2 px-0.5 text-[11px] text-ink-2 tabular">
        {shown === undefined ? (
          <span>{label}</span>
        ) : (
          <>
            <span className="font-semibold text-ink">{formatBin(shown)}</span>
            <span>{shown.total} closed</span>
            {STACK_ORDER.filter((reason) => shown.counts[reason] > 0).map((reason) => {
              const style = OUTCOMES.find((o) => o.reason === reason);
              return (
                <span key={reason} className="inline-flex items-center gap-1">
                  {style !== undefined && <OutcomeMarker color={style.color} shape={style.shape} size={7} />}
                  {shown.counts[reason]}
                </span>
              );
            })}
          </>
        )}
      </div>
      <div ref={ref} style={{ height }}>
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label={label} onPointerLeave={() => setActive(undefined)}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {ticks.map((tick) => (
                <g key={tick} transform={`translate(0,${y(tick)})`}>
                  <line x2={innerWidth} stroke="var(--grid)" />
                  <text x={-4} dy="0.32em" textAnchor="end" fontSize={10} fill="var(--muted)" className="tabular">
                    {tick}
                  </text>
                </g>
              ))}
              {bins.map((bin, i) => {
                let base = 0;
                const left = x(i) ?? 0;
                return (
                  <g key={bin.x0} onPointerEnter={() => setActive(i)} onClick={() => setActive(i)} opacity={active === undefined || active === i ? 1 : 0.55}>
                    <rect x={left} width={x.bandwidth()} height={innerHeight} fill="transparent" />
                    {STACK_ORDER.map((reason) => {
                      const count = bin.counts[reason];
                      if (count === 0) {
                        return null;
                      }
                      const style = OUTCOMES.find((o) => o.reason === reason);
                      const top = y(base + count);
                      const h = y(base) - top;
                      base += count;
                      // A 1px surface gap between stacked segments keeps adjacent fills apart.
                      return <rect key={reason} x={left} y={top} width={x.bandwidth()} height={Math.max(0, h - 1)} fill={style?.color} rx={1} />;
                    })}
                    {i % labelEvery === 0 && (
                      <text x={left + x.bandwidth() / 2} y={innerHeight + 13} textAnchor="middle" fontSize={10} fill="var(--muted)" className="tabular">
                        {formatBin(bin).split('–')[0]}
                      </text>
                    )}
                  </g>
                );
              })}
              <line y1={innerHeight} y2={innerHeight} x2={innerWidth} stroke="var(--axis)" />
            </g>
          </svg>
        )}
      </div>
    </div>
  );
}
